import { noop } from '@capsicum/common';
import { motion } from 'framer-motion';
import { type FC } from 'react';
import ClickAwayListener from 'react-click-away-listener';

import { useBodyScrollLock } from '../../../hooks/helpers/useBodyScrollLock';
import { useWindowSize } from '../../../hooks/helpers/useWindowSize';
import { type DesktopModalWrapperProps, desktopSizes } from '../../../models/modal';

import * as S from './DesktopModalWrapper.styled';

export const DesktopModalWrapper: FC<DesktopModalWrapperProps> = ({
	background,
	child,
	closeIconColor,
	closeModal,
	detached = true,
	footer,
	fullHeight = false,
	inView,
	isClosable = true,
	justClosed,
	size = 'default',
	subHeader,
	testID = 'common-desktopModalWrapper'
}) => {
	useBodyScrollLock();
	const { dimensions } = useWindowSize();

	const modalHeight = dimensions.height * desktopSizes[size].heightRatio;

	return (
		<S.Background
			animate={inView && !justClosed ? 'show' : 'hide'}
			as={motion.div}
			data-testid={testID}
			initial="hide"
			variants={{
				hide: {
					background: 'rgb(0, 22, 37, 0)'
				},
				show: {
					background: 'rgb(0, 22, 37, 0.8)'
				}
			}}
		>
			<ClickAwayListener onClickAway={isClosable ? () => closeModal?.() : noop}>
				<S.Foreground
					$background={background}
					$detached={detached}
					$fullHeight={fullHeight}
					$modalHeight={modalHeight}
					$showCloseButton={isClosable}
					$size={desktopSizes[size].width}
					animate={inView && !justClosed ? { opacity: 1 } : { opacity: 0 }}
					as={motion.div}
					initial={{ opacity: 0 }}
				>
					<S.Header $showCloseButton={isClosable}>
						{isClosable && (
							<S.Close
								$color={closeIconColor}
								data-testid={`${testID}-closeIcon`}
								onClick={() => closeModal?.()}
							/>
						)}
					</S.Header>
					{subHeader && <S.SubHeader>{subHeader}</S.SubHeader>}
					<S.Content>{child}</S.Content>
					{footer && <S.Footer>{footer}</S.Footer>}
				</S.Foreground>
			</ClickAwayListener>
		</S.Background>
	);
};
